/**
 * RTK Query API Slice
 * Centralized API management using RTK Query
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { errorHandler } from '../../utils/errorHandler';
import { authManager } from '../../utils/authManager';

// Base query with authentication and error handling
const baseQuery = fetchBaseQuery({
    baseUrl:
        process.env.REACT_APP_API_URL ||
        (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1'),
    prepareHeaders: (headers, { getState }) => {
        // Get token from unified auth manager
        const token = authManager.getToken();

        if (token) {
            headers.set('authorization', `Bearer ${token}`);
        }

        headers.set('content-type', 'application/json');
        return headers;
    }
});

// Enhanced base query with error handling
const baseQueryWithErrorHandling = async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions);

    // Handle errors
    if (result.error) {
        const parsedError = errorHandler.handle(result.error, {
            context: 'RTK Query',
            endpoint: args.url || args,
            method: args.method || 'GET'
        });

        // Update error with parsed information
        result.error = {
            ...result.error,
            parsedError,
            userMessage: parsedError.userMessage
        };
    }

    return result;
};

// Create API slice
export const apiSlice = createApi({
    reducerPath: 'api',
    baseQuery: baseQueryWithErrorHandling,
    tagTypes: ['User', 'Project', 'Environment', 'Server', 'Permission', 'Role', 'Billing', 'Storage', 'Database', 'Workflow', 'Airflow', 'Chatbot', 'ModelServing', 'Auth', 'ProjectDocument', 'Menu', 'Datahub', 'Lakehouse', 'ResearchDocs'],
    endpoints: (builder) => ({
        // Health check
        healthCheck: builder.query({
            query: () => '/health/check',
            providesTags: ['Health']
        }),

        // User endpoints
        getCurrentUser: builder.query({
            query: () => '/users/me',
            providesTags: ['User']
        }),

        updateUser: builder.mutation({
            query: (userData) => ({
                url: '/users/me',
                method: 'PATCH',
                body: userData
            }),
            invalidatesTags: ['User']
        }),

        // Superadmin: list & update users (RBAC)
        getUsers: builder.query({
            query: ({ skip = 0, limit = 100 } = {}) => ({ url: '/users/', params: { skip, limit } }),
            providesTags: ['User']
        }),
        updateUserById: builder.mutation({
            query: ({ userId, ...body }) => ({
                url: `/users/${userId}`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: ['User']
        }),

        // Auth (login)
        loginAccessToken: builder.mutation({
            queryFn: async (formData, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const res = await fetch(`${baseUrl}/login/access-token`, {
                    method: 'POST',
                    headers: { Accept: 'application/json' },
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json();
                return { data };
            }
        }),

        loginTestToken: builder.query({
            query: () => ({ url: '/login/test-token', method: 'POST' }),
            providesTags: ['User']
        }),

        // Project endpoints (arg có thể là userToken string hoặc { userToken, refetch } để ép refetch sau khi xóa)
        getProjects: builder.query({
            query: (arg) => {
                const userToken = typeof arg === 'object' && arg != null && 'userToken' in arg ? arg.userToken : arg;
                const refetch = typeof arg === 'object' && arg != null && arg.refetch;
                let url = '/projects/list';
                const params = [];
                if (userToken != null && userToken !== '') params.push(`user_token=${encodeURIComponent(userToken)}`);
                if (refetch) params.push(`_=${refetch}`);
                if (params.length) url += '?' + params.join('&');
                return url;
            },
            providesTags: ['Project']
        }),

        createProject: builder.mutation({
            query: (projectData) => ({
                url: '/projects/create',
                method: 'POST',
                body: projectData
            }),
            invalidatesTags: ['Project']
        }),

        getProjectMembers: builder.query({
            query: (projectName) => ({ url: `/projects/${encodeURIComponent(projectName)}/members` }),
            providesTags: (result, err, projectName) => [{ type: 'Project', id: `members-${projectName}` }]
        }),
        addProjectMember: builder.mutation({
            query: ({ projectName, email, user_id }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/members`,
                method: 'POST',
                body: email != null ? { email } : { user_id }
            }),
            invalidatesTags: (result, err, { projectName }) => ['Project', { type: 'Project', id: `members-${projectName}` }]
        }),
        removeProjectMember: builder.mutation({
            query: ({ projectName, userId }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/members/${userId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (result, err, { projectName }) => ['Project', { type: 'Project', id: `members-${projectName}` }]
        }),

        getProject: builder.query({
            query: ({ projectName, userToken }) => `/projects/${projectName}?user_token=${userToken}`,
            providesTags: ['Project']
        }),

        updateProject: builder.mutation({
            query: ({ projectName, projectData }) => ({
                url: `/projects/${projectName}`,
                method: 'PUT',
                body: projectData
            }),
            invalidatesTags: ['Project']
        }),

        deleteProject: builder.mutation({
            query: (projectName) => ({
                url: `/projects/${encodeURIComponent(projectName)}`,
                method: 'DELETE'
            }),
            // Cập nhật cache getProjects thay vì invalidate để tránh gọi /projects/list nhiều lần
            async onQueryStarted(projectName, { dispatch, getState, queryFulfilled }) {
                const userToken =
                    typeof window !== 'undefined' ? localStorage.getItem('user_token') : undefined;
                const patchList = (draft) => {
                    if (!draft) return;
                    const list = Array.isArray(draft) ? draft : draft?.projects;
                    if (!Array.isArray(list)) return;
                    const name = (projectName || '').toLowerCase();
                    if (Array.isArray(draft)) {
                        return list.filter(
                            (p) => (p.project_name || p.projectName || '').toLowerCase() !== name
                        );
                    }
                    draft.projects = list.filter(
                        (p) => (p.project_name || p.projectName || '').toLowerCase() !== name
                    );
                };
                try {
                    await queryFulfilled;
                    if (userToken != null)
                        dispatch(apiSlice.util.updateQueryData('getProjects', userToken, patchList));
                    dispatch(apiSlice.util.updateQueryData('getProjects', undefined, patchList));
                } catch {}
            }
        }),

        // Project API Gateway (api-keys, metrics) – backend vẫn dùng user_token query
        getProjectApiKeys: builder.query({
            query: ({ projectName, user_token }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/api-keys`,
                params: user_token != null ? { user_token } : {}
            }),
            providesTags: (result, err, { projectName }) => [{ type: 'Project', id: `api-keys-${projectName}` }]
        }),
        createProjectApiKey: builder.mutation({
            query: ({ projectName, user_token, ...body }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/api-keys`,
                method: 'POST',
                params: user_token != null ? { user_token } : {},
                body
            }),
            invalidatesTags: (result, err, { projectName }) => ['Project', { type: 'Project', id: `api-keys-${projectName}` }]
        }),
        deleteProjectApiKey: builder.mutation({
            query: ({ projectName, keyId, user_token }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/api-keys/${encodeURIComponent(keyId)}`,
                method: 'DELETE',
                params: user_token != null ? { user_token } : {}
            }),
            invalidatesTags: (result, err, { projectName }) => ['Project', { type: 'Project', id: `api-keys-${projectName}` }]
        }),
        getProjectApiKeyPlaintext: builder.query({
            query: ({ projectName, keyId }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/api-keys/${encodeURIComponent(keyId)}/plaintext`
            })
        }),
        getProjectApiGatewayMetrics: builder.query({
            query: ({ projectName, user_token }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/api-gateway/metrics`,
                params: user_token != null ? { user_token } : {}
            }),
            providesTags: (result, err, { projectName }) => [{ type: 'Project', id: `api-metrics-${projectName}` }]
        }),

        // API Hub: services, routes, test, deploy (JWT auth)
        getApihubServices: builder.query({
            query: (projectName) => ({ url: `/projects/${encodeURIComponent(projectName)}/apihub/services` }),
            providesTags: (result, err, projectName) => [{ type: 'Project', id: `apihub-services-${projectName}` }]
        }),
        uploadApihubService: builder.mutation({
            queryFn: async ({ projectName, file }, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const formData = new FormData();
                formData.append('file', file);
                const res = await fetch(`${baseUrl}/projects/${encodeURIComponent(projectName)}/apihub/services`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json();
                return { data };
            },
            invalidatesTags: (result, err, { projectName }) => [{ type: 'Project', id: `apihub-services-${projectName}` }]
        }),
        deleteApihubService: builder.mutation({
            query: ({ projectName, filename }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/apihub/services/${encodeURIComponent(filename)}`,
                method: 'DELETE'
            }),
            invalidatesTags: (result, err, { projectName }) => [{ type: 'Project', id: `apihub-services-${projectName}` }]
        }),
        getApihubServiceContent: builder.query({
            query: ({ projectName, filename }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/apihub/services/${encodeURIComponent(filename)}/content`
            }),
            providesTags: (result, err, { projectName, filename }) => [{ type: 'Project', id: `apihub-service-content-${projectName}-${filename}` }]
        }),
        updateApihubServiceContent: builder.mutation({
            query: ({ projectName, filename, content }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/apihub/services/${encodeURIComponent(filename)}/content`,
                method: 'PUT',
                body: { content }
            }),
            invalidatesTags: (result, err, { projectName }) => [{ type: 'Project', id: `apihub-services-${projectName}` }, { type: 'Project', id: `apihub-service-content-${projectName}` }]
        }),
        putApihubRoutesFull: builder.mutation({
            query: ({ projectName, endpoints }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/apihub/routes`,
                method: 'PUT',
                body: { endpoints }
            }),
            invalidatesTags: (result, err, { projectName }) => [{ type: 'Project', id: `apihub-routes-${projectName}` }]
        }),
        getApihubRoutes: builder.query({
            query: (projectName) => ({ url: `/projects/${encodeURIComponent(projectName)}/apihub/routes` }),
            providesTags: (result, err, projectName) => [{ type: 'Project', id: `apihub-routes-${projectName}` }]
        }),
        createApihubRoute: builder.mutation({
            query: ({ projectName, body }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/apihub/routes`,
                method: 'POST',
                body
            }),
            invalidatesTags: (result, err, { projectName }) => [{ type: 'Project', id: `apihub-routes-${projectName}` }]
        }),
        updateApihubRoute: builder.mutation({
            query: ({ projectName, routeId, body }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/apihub/routes/${encodeURIComponent(routeId)}`,
                method: 'PUT',
                body
            }),
            invalidatesTags: (result, err, { projectName }) => [{ type: 'Project', id: `apihub-routes-${projectName}` }]
        }),
        deleteApihubRoute: builder.mutation({
            query: ({ projectName, routeId }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/apihub/routes/${encodeURIComponent(routeId)}`,
                method: 'DELETE'
            }),
            invalidatesTags: (result, err, { projectName }) => [{ type: 'Project', id: `apihub-routes-${projectName}` }]
        }),
        testApihubEndpoint: builder.mutation({
            query: ({ projectName, body }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/apihub/test`,
                method: 'POST',
                body
            })
        }),
        deployApihub: builder.mutation({
            query: (projectName) => ({
                url: `/projects/${encodeURIComponent(projectName)}/apihub/deploy`,
                method: 'POST'
            }),
            invalidatesTags: (result, err, projectName) => [{ type: 'Project', id: `apihub-deploy-${projectName}` }]
        }),
        undeployApihub: builder.mutation({
            query: (projectName) => ({
                url: `/projects/${encodeURIComponent(projectName)}/apihub/deploy`,
                method: 'DELETE'
            }),
            invalidatesTags: (result, err, projectName) => [{ type: 'Project', id: `apihub-deploy-${projectName}` }]
        }),
        getApihubDeployStatus: builder.query({
            query: (projectName) => ({ url: `/projects/${encodeURIComponent(projectName)}/apihub/deploy` }),
            providesTags: (result, err, projectName) => [{ type: 'Project', id: `apihub-deploy-${projectName}` }]
        }),
        seedApihubSamples: builder.mutation({
            query: (projectName) => ({
                url: `/projects/${encodeURIComponent(projectName)}/apihub/seed-samples`,
                method: 'POST'
            }),
            invalidatesTags: (result, err, projectName) => [
                { type: 'Project', id: `apihub-services-${projectName}` },
                { type: 'Project', id: `apihub-routes-${projectName}` }
            ]
        }),
        syncApihubSamples: builder.mutation({
            query: (projectName) => ({
                url: `/projects/${encodeURIComponent(projectName)}/apihub/sync-samples`,
                method: 'POST'
            })
        }),
        createApihubServiceFromTemplate: builder.mutation({
            query: ({ projectName, filename, method }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/apihub/services/from-template`,
                method: 'POST',
                body: { filename: (filename || '').trim() ? (filename.endsWith('.py') ? filename : filename + '.py') : 'handler.py', method: method || 'POST' }
            }),
            invalidatesTags: (result, err, { projectName }) => [{ type: 'Project', id: `apihub-services-${projectName}` }]
        }),
        validateApihubService: builder.query({
            query: ({ projectName, filename, entrypoint }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/apihub/services/${encodeURIComponent(filename)}/validate`,
                params: entrypoint ? { entrypoint } : {}
            }),
            providesTags: (result, err, { projectName, filename }) => [{ type: 'Project', id: `apihub-service-validate-${projectName}-${filename}` }]
        }),

        // Project Data Sources (Big Data / Lakehouse: MongoDB, Postgres, MinIO, Firebase)
        getProjectDataSources: builder.query({
            query: (projectName) => ({ url: `/projects/${encodeURIComponent(projectName)}/data-sources` }),
            providesTags: (result, err, projectName) => [{ type: 'Project', id: `data-sources-${projectName}` }]
        }),
        createProjectDataSource: builder.mutation({
            query: ({ projectName, body }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/data-sources`,
                method: 'POST',
                body
            }),
            invalidatesTags: (result, err, { projectName }) => [{ type: 'Project', id: `data-sources-${projectName}` }]
        }),
        updateProjectDataSource: builder.mutation({
            query: ({ projectName, sourceId, body }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/data-sources/${sourceId}`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: (result, err, { projectName }) => [{ type: 'Project', id: `data-sources-${projectName}` }]
        }),
        deleteProjectDataSource: builder.mutation({
            query: ({ projectName, sourceId }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/data-sources/${sourceId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (result, err, { projectName }) => [{ type: 'Project', id: `data-sources-${projectName}` }]
        }),
        syncLakehouseSamples: builder.mutation({
            query: (projectName) => ({
                url: `/projects/${encodeURIComponent(projectName)}/lakehouse-sync`,
                method: 'POST'
            }),
            invalidatesTags: (result, err, projectName) => [{ type: 'Project', id: `data-sources-${projectName}` }]
        }),
        fetchProjectDataSource: builder.query({
            query: ({ projectName, sourceId, params }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/data-sources/${sourceId}/fetch`,
                params: params || {}
            }),
            providesTags: (result, err, { projectName, sourceId }) => [
                { type: 'Project', id: `data-sources-${projectName}` },
                { type: 'Project', id: `data-fetch-${projectName}-${sourceId}` }
            ]
        }),

        // Jupyter container (Docker-based JupyterLab)
        jupyterStartContainer: builder.mutation({
            query: (projectId) => ({
                url: `/jupyter/projects/${encodeURIComponent(projectId)}/start`,
                method: 'POST',
                body: {}
            }),
            invalidatesTags: ['Project']
        }),
        jupyterContainerStatus: builder.query({
            query: (projectId) => ({ url: `/jupyter/projects/${encodeURIComponent(projectId)}/status` }),
            providesTags: (result, err, projectId) => [{ type: 'Project', id: `jupyter-${projectId}` }]
        }),
        jupyterStopContainer: builder.mutation({
            query: (projectId) => ({
                url: `/jupyter/projects/${encodeURIComponent(projectId)}/stop`,
                method: 'POST',
                body: {}
            }),
            invalidatesTags: ['Project']
        }),
        jupyterRestartContainer: builder.mutation({
            query: (projectId) => ({
                url: `/jupyter/projects/${encodeURIComponent(projectId)}/restart`,
                method: 'POST',
                body: {}
            }),
            invalidatesTags: ['Project']
        }),
        jupyterContainerLogs: builder.query({
            query: ({ projectId, tail = 100 }) => ({
                url: `/jupyter/projects/${encodeURIComponent(projectId)}/logs`,
                params: { tail }
            }),
            providesTags: (result, err, { projectId }) => [{ type: 'Project', id: `jupyter-logs-${projectId}` }]
        }),
        jupyterRemoveContainer: builder.mutation({
            query: ({ projectId, force = false }) => ({
                url: `/jupyter/projects/${encodeURIComponent(projectId)}/remove`,
                method: 'DELETE',
                params: { force }
            }),
            invalidatesTags: ['Project']
        }),
        jupyterMyContainers: builder.query({
            query: () => ({ url: '/jupyter/my-containers' }),
            providesTags: ['Project']
        }),

        // BI (Superset) shared – access URL + project context
        getBiAccess: builder.query({
            query: (projectName) => ({
                url: '/bi/access',
                params: { project_name: projectName }
            }),
            providesTags: (result, err, projectName) => [{ type: 'Project', id: `bi-${projectName}` }]
        }),

        // JupyterHub auth (token, workspace URL)
        jupyterhubGenerateToken: builder.mutation({
            query: (body) => ({
                url: '/jupyterhub-api/auth/generate-token',
                method: 'POST',
                body
            })
        }),
        jupyterhubWorkspaceUrl: builder.query({
            query: ({ user_token, project_name }) => ({
                url: '/jupyterhub-api/auth/workspace-url',
                params: { user_token, project_name }
            })
        }),
        jupyterhubValidateToken: builder.mutation({
            query: (body) => ({
                url: '/jupyterhub-api/auth/validate-token',
                method: 'POST',
                body
            })
        }),

        // Create folder in project (v1)
        createProjectFolder: builder.mutation({
            query: ({ projectName, folder_name, parent_path = '/' }) => ({
                url: `/projects/${encodeURIComponent(projectName)}/folders`,
                method: 'POST',
                body: { folder_name, parent_path }
            }),
            invalidatesTags: ['ProjectDocument', 'Project']
        }),

        // Upload documents to project folder (FormData: files, user_token, project_name, folder_name, labels)
        uploadProjectDocumentFolder: builder.mutation({
            queryFn: async (formData, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const res = await fetch(`${baseUrl}/projects/document/folder`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json().catch(() => ({}));
                return { data };
            },
            invalidatesTags: ['ProjectDocument', 'Project']
        }),

        // Upload project avatar (FormData: file, user_token, storage, vector_id, project_name)
        uploadProjectAvatar: builder.mutation({
            queryFn: async (formData, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const res = await fetch(`${baseUrl}/api/upload/projectavatar`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json().catch(() => ({}));
                return { data };
            },
            invalidatesTags: ['Project']
        }),

        // Task status (CV / training)
        getTaskStatus: builder.query({
            query: (taskId) => ({ url: `/cv/tasks/${encodeURIComponent(taskId)}` }),
            providesTags: (result, err, taskId) => [{ type: 'Project', id: `task-${taskId}` }]
        }),

        // Chat / DocsGPT: conversations (backend GET /conversations trả [] stub)
        getConversationsList: builder.query({
            query: ({ project = 'document', user_token } = {}) => ({
                url: '/conversations',
                params: { project, ...(user_token != null && { user_token }) }
            }),
            providesTags: ['Datahub']
        }),
        getConversation: builder.query({
            query: (index) => ({ url: `/conversations/${encodeURIComponent(index)}` }),
            providesTags: (result, err, index) => [{ type: 'Datahub', id: `conversation-${index}` }]
        }),
        updateConversation: builder.mutation({
            query: ({ id, ...body }) => ({
                url: `/conversations/${encodeURIComponent(id)}`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: (result, err, { id }) => ['Datahub', { type: 'Datahub', id: `conversation-${id}` }]
        }),
        deleteConversation: builder.mutation({
            query: (id) => ({
                url: `/conversations/${encodeURIComponent(id)}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Datahub']
        }),
        getDeleteOld: builder.query({
            query: (path) => ({ url: '/api/delete_old', params: path ? { path } : {} })
        }),
        postChatFeedback: builder.mutation({
            query: (body) => ({
                url: '/api/feedback',
                method: 'POST',
                body
            })
        }),

        // Speech-to-text (whisper) - FormData
        postS2tWhisper: builder.mutation({
            queryFn: async (formData, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const res = await fetch(`${baseUrl}/api/s2t/whisper`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json().catch(() => ({}));
                return { data };
            }
        }),

        // Billing usage (POST form - Firebase flow)
        postBillingUsage: builder.mutation({
            queryFn: async (formData, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const res = await fetch(`${baseUrl}/billings/usage`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json();
                return { data };
            },
            invalidatesTags: ['Billing']
        }),

        // Project documents (uploaded files list/view/delete/download)
        getProjectDocuments: builder.query({
            query: ({ project_name, folder_path = '', user_token } = {}) => {
                const params = new URLSearchParams();
                if (user_token) params.set('user_token', user_token);
                params.set('project_name', project_name || '');
                params.set('folder_path', folder_path || '');
                return `/projects/document?${params.toString()}`;
            },
            providesTags: ['ProjectDocument']
        }),

        // Upload files to project document folder (FormData: user_token, project_name, folder_name, files, labels)
        uploadProjectDocument: builder.mutation({
            queryFn: async (formData, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const res = await fetch(`${baseUrl}/projects/document/upload`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json().catch(() => ({}));
                return { data };
            },
            invalidatesTags: ['ProjectDocument']
        }),

        deleteProjectDocument: builder.mutation({
            queryFn: async (formData, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const res = await fetch(`${baseUrl}/projects/document/delete`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json().catch(() => ({}));
                return { data };
            },
            invalidatesTags: ['ProjectDocument']
        }),

        // Research Docs (Knowledge) - RAG collections
        getResearchDocsList: builder.query({
            // params: { project_name?: string }
            query: ({ project_name } = {}) => ({
                url: '/research-docs/list',
                params: project_name ? { project_name } : undefined
            }),
            providesTags: ['ResearchDocs']
        }),
        createResearchDoc: builder.mutation({
            query: (body) => ({ url: '/research-docs/create', method: 'POST', body }),
            invalidatesTags: ['ResearchDocs']
        }),
        getResearchDoc: builder.query({
            query: (id) => ({ url: `/research-docs/${id}` }),
            providesTags: (result, err, id) => [{ type: 'ResearchDocs', id }]
        }),
        updateResearchDoc: builder.mutation({
            query: ({ id, ...body }) => ({ url: `/research-docs/${id}`, method: 'PUT', body }),
            invalidatesTags: (result, err, { id }) => ['ResearchDocs', { type: 'ResearchDocs', id }]
        }),
        deleteResearchDoc: builder.mutation({
            query: (id) => ({ url: `/research-docs/${id}`, method: 'DELETE' }),
            invalidatesTags: ['ResearchDocs']
        }),
        getResearchDocDocuments: builder.query({
            query: (collectionId) => ({ url: `/research-docs/${collectionId}/documents` }),
            providesTags: (result, err, id) => [{ type: 'ResearchDocs', id: `documents-${id}` }]
        }),
        addResearchDocDocuments: builder.mutation({
            query: ({ collectionId, file_ids }) => ({
                url: `/research-docs/${collectionId}/documents`,
                method: 'POST',
                body: { file_ids }
            }),
            invalidatesTags: (result, err, { collectionId }) => ['ResearchDocs', { type: 'ResearchDocs', id: collectionId }, { type: 'ResearchDocs', id: `documents-${collectionId}` }]
        }),
        getResearchDocTaskStatus: builder.query({
            query: (taskId) => ({ url: `/research-docs/task-status?task_id=${encodeURIComponent(taskId)}` }),
            providesTags: (result, err, taskId) => [{ type: 'ResearchDocs', id: `task-${taskId}` }]
        }),
        removeResearchDocDocument: builder.mutation({
            query: ({ collectionId, documentId }) => ({
                url: `/research-docs/${collectionId}/documents/${documentId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (result, err, { collectionId }) => ['ResearchDocs', { type: 'ResearchDocs', id: `documents-${collectionId}` }]
        }),
        askResearchDoc: builder.mutation({
            query: ({ collectionId, query, chat_history, top_k, llm_model_override, conversation_id, save_response, attachment_file_ids, edit_file_id }) => ({
                url: `/research-docs/${collectionId}/ask`,
                method: 'POST',
                body: {
                    query,
                    chat_history: chat_history ?? undefined,
                    top_k: top_k ?? 5,
                    llm_model_override: llm_model_override ?? undefined,
                    conversation_id: conversation_id ?? undefined,
                    save_response: save_response !== false,
                    attachment_file_ids: attachment_file_ids ?? undefined,
                    edit_file_id: edit_file_id ?? undefined
                }
            }),
            invalidatesTags: (result, err, { collectionId }) =>
                result?.conversation_id ? ['ResearchDocs', { type: 'ResearchDocs', id: `conversations-${collectionId}` }] : []
        }),
        getResearchConversations: builder.query({
            query: (collectionId) => ({
                url: '/research-docs/conversations',
                params: collectionId ? { collection_id: collectionId } : {}
            }),
            providesTags: (result, err, collectionId) => [{ type: 'ResearchDocs', id: `conversations-${collectionId}` }]
        }),
        getResearchConversationDetail: builder.query({
            query: (conversationId) => ({
                url: `/research-docs/conversations/${encodeURIComponent(conversationId)}`
            }),
            providesTags: (result, err, conversationId) => [{ type: 'ResearchDocs', id: `conversation-${conversationId}` }]
        }),
        createResearchConversation: builder.mutation({
            query: (body) => ({
                url: '/research-docs/conversations',
                method: 'POST',
                body
            }),
            invalidatesTags: (result, err, body) =>
                result?.collection_id ? [{ type: 'ResearchDocs', id: `conversations-${result.collection_id}` }] : []
        }),
        deleteResearchConversation: builder.mutation({
            query: (conversationId) => ({
                url: `/research-docs/conversations/${encodeURIComponent(conversationId)}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['ResearchDocs']
        }),

        viewProjectFile: builder.mutation({
            query: (body) => ({
                url: '/projects/view-file',
                method: 'POST',
                body
            })
        }),

        getProjectUploadBlob: builder.query({
            queryFn: async ({ project_name, file_path }, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const res = await fetch(
                    `${baseUrl}/projects/uploads?file_path=${encodeURIComponent(file_path)}`,
                    { headers: token ? { Authorization: `Bearer ${token}`, 'project-name': project_name } : { 'project-name': project_name } }
                );
                if (!res.ok) return { error: { status: res.status, data: { detail: res.statusText } } };
                const blob = await res.blob();
                return { data: blob };
            }
        }),

        // Project MongoDB
        getProjectMongodbTables: builder.query({
            query: (project_name) => `/projects/${encodeURIComponent(project_name)}/mongodb/tables`,
            providesTags: ['Project']
        }),

        createProjectMongodbTables: builder.mutation({
            queryFn: async (formData, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const res = await fetch(`${baseUrl}/projects/mongodb/tables`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json();
                return { data };
            },
            invalidatesTags: ['Project']
        }),

        deleteProjectMongodbTables: builder.mutation({
            queryFn: async (formData, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const res = await fetch(`${baseUrl}/projects/mongodb/tables`, {
                    method: 'DELETE',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json();
                return { data };
            },
            invalidatesTags: ['Project']
        }),

        getProjectMongodbDocuments: builder.query({
            query: (params) => {
                const q = new URLSearchParams(params);
                return `/projects/mongodb/documents?${q.toString()}`;
            },
            providesTags: ['Project']
        }),

        updateProjectMongodbDocuments: builder.mutation({
            queryFn: async (formData, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const res = await fetch(`${baseUrl}/projects/mongodb/documents/update`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                return { data: {} };
            },
            invalidatesTags: ['Project']
        }),

        deleteProjectMongodbDocuments: builder.mutation({
            queryFn: async (formData, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const res = await fetch(`${baseUrl}/projects/mongodb/documents/delete`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                return { data: {} };
            },
            invalidatesTags: ['Project']
        }),

        deleteHubUser: builder.mutation({
            query: (username) => ({
                url: `/jupyterhub/user/delete?username=${encodeURIComponent(username)}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Project']
        }),

        createAirUser: builder.mutation({
            queryFn: async (formData, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const res = await fetch(`${baseUrl}/air/user/create`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json();
                return { data };
            },
            invalidatesTags: ['Project']
        }),

        deleteAirUser: builder.mutation({
            queryFn: async (formData, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const res = await fetch(`${baseUrl}/air/user/delete`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json();
                return { data };
            },
            invalidatesTags: ['Project']
        }),

        getProjectAirDags: builder.mutation({
            queryFn: async (formData, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const res = await fetch(`${baseUrl}/projects/air/dags`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json();
                return { data };
            }
        }),

        insertProjectAirDag: builder.mutation({
            queryFn: async (formData, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const res = await fetch(`${baseUrl}/projects/air/dags/insert`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json();
                return { data };
            },
            invalidatesTags: ['Project']
        }),

        deleteProjectAirDag: builder.mutation({
            queryFn: async (formData, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const res = await fetch(`${baseUrl}/projects/air/dags/delete`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json();
                return { data };
            },
            invalidatesTags: ['Project']
        }),

        fetchProjectAirDags: builder.mutation({
            queryFn: async (formData, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const res = await fetch(`${baseUrl}/projects/air/dags/fetch`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json();
                return { data };
            }
        }),

        fetchProjectAirDagsNodes: builder.mutation({
            queryFn: async (formData, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const res = await fetch(`${baseUrl}/projects/air/dags/nodes/fetch`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json();
                return { data };
            }
        }),

        fetchProjectAirDagsEdges: builder.mutation({
            queryFn: async (formData, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const res = await fetch(`${baseUrl}/projects/air/dags/edges/fetch`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json();
                return { data };
            }
        }),

        // JupyterHub endpoints
        getEnvironments: builder.query({
            query: ({ userToken, projectName }) => `/jupyterhub/environments?user_token=${userToken}&project_name=${projectName}`,
            providesTags: ['Environment']
        }),

        createEnvironment: builder.mutation({
            query: (environmentData) => ({
                url: '/jupyterhub/environments',
                method: 'POST',
                body: environmentData
            }),
            invalidatesTags: ['Environment']
        }),

        deleteEnvironment: builder.mutation({
            query: (envId) => ({
                url: `/jupyterhub/environments/${envId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Environment']
        }),

        setDefaultEnvironment: builder.mutation({
            query: (envId) => ({
                url: `/jupyterhub/environments/${envId}/default`,
                method: 'PUT',
                body: {}
            }),
            invalidatesTags: ['Environment']
        }),

        getEnvironmentPackages: builder.query({
            query: (envId) => `/jupyterhub/environments/${envId}/packages`,
            providesTags: ['Environment']
        }),

        installPackages: builder.mutation({
            query: ({ envId, packages, channel = 'conda-forge' }) => ({
                url: `/jupyterhub/environments/${envId}/packages`,
                method: 'POST',
                body: { packages, channel }
            }),
            invalidatesTags: ['Environment']
        }),

        searchPackages: builder.query({
            query: ({ query, channel = 'conda-forge' }) => `/jupyterhub/environments/search-packages?query=${query}&channel=${channel}`,
            providesTags: ['Environment']
        }),

        exportEnvironment: builder.query({
            query: (envId) => `/jupyterhub/environments/${envId}/export`,
            providesTags: ['Environment']
        }),

        importEnvironment: builder.mutation({
            query: (importData) => ({
                url: '/jupyterhub/environments/import',
                method: 'POST',
                body: importData
            }),
            invalidatesTags: ['Environment']
        }),

        cloneEnvironment: builder.mutation({
            query: ({ envId, newEnvName, userToken, projectName }) => ({
                url: `/jupyterhub/environments/${envId}/clone`,
                method: 'POST',
                body: { new_env_name: newEnvName, user_token: userToken, project_name: projectName }
            }),
            invalidatesTags: ['Environment']
        }),

        // Server endpoints
        getServers: builder.query({
            query: ({ userToken, projectName }) => `/jupyterhub/servers?user_token=${userToken}&project_name=${projectName}`,
            providesTags: ['Server']
        }),

        startServer: builder.mutation({
            query: (serverId) => ({
                url: `/jupyterhub/servers/${serverId}/start`,
                method: 'POST'
            }),
            invalidatesTags: ['Server']
        }),

        stopServer: builder.mutation({
            query: (serverId) => ({
                url: `/jupyterhub/servers/${serverId}/stop`,
                method: 'POST'
            }),
            invalidatesTags: ['Server']
        }),

        deleteServer: builder.mutation({
            query: (serverId) => ({
                url: `/jupyterhub/servers/${serverId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Server']
        }),

        // RBAC endpoints
        getPermissions: builder.query({
            query: (category = null) => (category ? `/rbac/permissions?category=${category}` : '/rbac/permissions'),
            providesTags: ['Permission']
        }),

        createPermission: builder.mutation({
            query: (permission) => ({
                url: '/rbac/permissions',
                method: 'POST',
                body: permission
            }),
            invalidatesTags: ['Permission']
        }),

        updatePermission: builder.mutation({
            query: ({ permissionId, permission }) => ({
                url: `/rbac/permissions/${permissionId}`,
                method: 'PUT',
                body: permission
            }),
            invalidatesTags: ['Permission']
        }),

        deletePermission: builder.mutation({
            query: (permissionId) => ({
                url: `/rbac/permissions/${permissionId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Permission']
        }),

        getRoles: builder.query({
            query: (organizationId = null) => (organizationId ? `/rbac/roles?organization_id=${organizationId}` : '/rbac/roles'),
            providesTags: ['Role']
        }),

        createRole: builder.mutation({
            query: (role) => ({
                url: '/rbac/roles',
                method: 'POST',
                body: role
            }),
            invalidatesTags: ['Role']
        }),

        updateRole: builder.mutation({
            query: ({ roleId, role }) => ({
                url: `/rbac/roles/${roleId}`,
                method: 'PUT',
                body: role
            }),
            invalidatesTags: ['Role']
        }),

        deleteRole: builder.mutation({
            query: (roleId) => ({
                url: `/rbac/roles/${roleId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Role']
        }),

        getRBACUsers: builder.query({
            query: (organizationId = null) => (organizationId ? `/rbac/users?organization_id=${organizationId}` : '/rbac/users'),
            providesTags: ['User']
        }),

        createUser: builder.mutation({
            query: (user) => ({
                url: '/rbac/users',
                method: 'POST',
                body: user
            }),
            invalidatesTags: ['User']
        }),

        updateRBACUser: builder.mutation({
            query: ({ userId, user }) => ({
                url: `/rbac/users/${userId}`,
                method: 'PUT',
                body: user
            }),
            invalidatesTags: ['User']
        }),

        deleteUser: builder.mutation({
            query: (userId) => ({
                url: `/rbac/users/${userId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['User']
        }),

        getUserPermissions: builder.query({
            query: (userId) => `/rbac/users/${userId}/permissions`,
            providesTags: ['Permission']
        }),

        assignRoleToUser: builder.mutation({
            query: ({ userId, roleId }) => ({
                url: `/rbac/users/${userId}/roles/${roleId}`,
                method: 'POST',
                body: {}
            }),
            invalidatesTags: ['User', 'Role']
        }),

        removeRoleFromUser: builder.mutation({
            query: ({ userId, roleId }) => ({
                url: `/rbac/users/${userId}/roles/${roleId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['User', 'Role']
        }),

        checkPermission: builder.mutation({
            query: ({ userId, resource, action, organizationId = null }) => ({
                url: '/rbac/check-permission',
                method: 'POST',
                body: { user_id: userId, resource, action, organization_id: organizationId }
            })
        }),

        // Billing endpoints
        getBillingUsage: builder.query({
            query: (userToken) => `/billings/usage?user_token=${userToken}`,
            providesTags: ['Billing']
        }),

        getBillingInvoices: builder.query({
            query: (userToken) => `/billings/invoices?user_token=${userToken}`,
            providesTags: ['Billing']
        }),

        // Storage endpoints (N2NAI: auth via Bearer, params match backend /storage/files)
        getStorageFiles: builder.query({
            query: (params = {}) => {
                const {
                    page = 1,
                    page_size = 50,
                    search,
                    storage_backend,
                    lifecycle_tier,
                    migration_status,
                    project_id,
                    folder_path
                } = params;
                const q = new URLSearchParams();
                if (page != null) q.set('page', String(page));
                if (page_size != null) q.set('page_size', String(page_size));
                if (search != null && search !== '') q.set('search', search);
                if (storage_backend) q.set('storage_backend', storage_backend);
                if (lifecycle_tier) q.set('lifecycle_tier', lifecycle_tier);
                if (migration_status) q.set('migration_status', migration_status);
                if (project_id != null && project_id !== '') q.set('project_id', String(project_id));
                if (folder_path != null && folder_path !== '') q.set('folder_path', folder_path);
                const query = q.toString();
                return query ? `/storage/files?${query}` : '/storage/files';
            },
            providesTags: ['Storage']
        }),

        uploadStorageFile: builder.mutation({
            queryFn: async ({ file, project_id }, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const formData = new FormData();
                formData.append('file', file);
                if (project_id) formData.append('project_id', String(project_id));
                const res = await fetch(`${baseUrl}/storage/files/upload`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json();
                return { data };
            },
            invalidatesTags: ['Storage']
        }),

        deleteStorageFile: builder.mutation({
            query: (fileId) => ({
                url: `/storage/files/${fileId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Storage']
        }),

        bulkDeleteStorageFiles: builder.mutation({
            query: (body) => ({
                url: '/storage/files/bulk/delete',
                method: 'POST',
                body: { file_ids: body.file_ids }
            }),
            invalidatesTags: ['Storage']
        }),

        updateStorageFileLifecycle: builder.mutation({
            query: ({ fileId, lifecycle_tier }) => ({
                url: `/storage/files/${fileId}/lifecycle`,
                method: 'PATCH',
                body: { lifecycle_tier }
            }),
            invalidatesTags: ['Storage']
        }),

        getStorageAnalytics: builder.query({
            query: () => '/storage/analytics',
            providesTags: ['Storage']
        }),

        getStorageMigrationStatus: builder.query({
            query: () => '/storage/migration/status',
            providesTags: ['Storage']
        }),

        startStorageMigration: builder.mutation({
            query: (body) => ({
                url: '/storage/migration/start',
                method: 'POST',
                body: body || {}
            }),
            invalidatesTags: ['Storage']
        }),

        getLakehouseStats: builder.query({
            query: () => '/storage/lakehouse/stats',
            providesTags: ['Storage']
        }),

        getLakehouseSources: builder.query({
            query: () => '/lakehouse/sources',
            providesTags: ['Lakehouse']
        }),
        getLakehouseSourceHealth: builder.query({
            query: (sourceType) => `/lakehouse/sources/${encodeURIComponent(sourceType)}`,
            providesTags: (result, error, sourceType) => [{ type: 'Lakehouse', id: sourceType }]
        }),

        getStorageFilePreview: builder.query({
            query: (fileId) => `/storage/files/${fileId}/preview`,
            providesTags: ['Storage']
        }),

        createStorageFileShare: builder.mutation({
            query: ({ fileId, ...body }) => ({
                url: `/storage/files/${fileId}/share`,
                method: 'POST',
                body: { file_id: fileId, ...body }
            }),
            invalidatesTags: ['Storage']
        }),

        getStorageFileShares: builder.query({
            query: (fileId) => `/storage/files/${fileId}/shares`,
            providesTags: ['Storage']
        }),

        deleteStorageFileShare: builder.mutation({
            query: ({ fileId, shareId }) => ({
                url: `/storage/files/${fileId}/share/${shareId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Storage']
        }),

        searchStorageFiles: builder.mutation({
            query: (body) => ({
                url: '/storage/files/search',
                method: 'POST',
                body
            }),
            invalidatesTags: ['Storage']
        }),

        createStorageFolder: builder.mutation({
            query: ({ folder_name, parent_path, project_id }) => ({
                url: project_id != null ? `/storage/folders?project_id=${encodeURIComponent(project_id)}` : '/storage/folders',
                method: 'POST',
                body: { folder_name, parent_path }
            }),
            invalidatesTags: ['Storage']
        }),

        getStorageFolders: builder.query({
            query: (params = {}) => {
                const q = new URLSearchParams();
                if (params.project_id != null) q.set('project_id', params.project_id);
                if (params.parent_path != null) q.set('parent_path', params.parent_path);
                const query = q.toString();
                return query ? `/storage/folders?${query}` : '/storage/folders';
            },
            providesTags: ['Storage']
        }),

        moveStorageFiles: builder.mutation({
            query: (body) => ({
                url: '/storage/files/move',
                method: 'POST',
                body
            }),
            invalidatesTags: ['Storage']
        }),

        copyStorageFiles: builder.mutation({
            query: (body) => ({
                url: '/storage/files/copy',
                method: 'POST',
                body
            }),
            invalidatesTags: ['Storage']
        }),

        deleteStorageFolder: builder.mutation({
            query: ({ folderPath, project_id }) => ({
                url: project_id != null
                    ? `/storage/folders/${encodeURIComponent(folderPath)}?project_id=${encodeURIComponent(project_id)}`
                    : `/storage/folders/${encodeURIComponent(folderPath)}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Storage']
        }),

        getStorageQuota: builder.query({
            query: () => '/storage/quota',
            providesTags: ['Storage']
        }),

        updateStorageQuota: builder.mutation({
            query: (body) => {
                const b = body || {};
                let url = '/storage/quota';
                if (b.user_id != null) url += `?user_id=${encodeURIComponent(b.user_id)}`;
                else if (b.tenant_id != null) url += `?tenant_id=${encodeURIComponent(b.tenant_id)}`;
                return { url, method: 'PUT', body: { quota_bytes: b.quota_bytes, max_file_count: b.max_file_count } };
            },
            invalidatesTags: ['Storage']
        }),

        // Legacy aliases (prefer names above)
        uploadFile: builder.mutation({
            queryFn: async ({ file, project_id, userToken, projectName, path }, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const formData = new FormData();
                formData.append('file', file);
                if (project_id) formData.append('project_id', String(project_id));
                if (userToken) formData.append('user_token', userToken);
                if (projectName) formData.append('project_name', projectName);
                if (path) formData.append('path', path);
                const res = await fetch(`${baseUrl}/storage/files/upload`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json();
                return { data };
            },
            invalidatesTags: ['Storage']
        }),

        deleteFile: builder.mutation({
            query: (fileId) => ({
                url: `/storage/files/${fileId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Storage']
        }),

        // Database endpoints
        getDatabaseStats: builder.query({
            query: ({ userToken, projectName }) => `/database/stats?user_token=${userToken}&project_name=${projectName}`,
            providesTags: ['Database']
        }),

        getCollections: builder.query({
            query: ({ userToken, projectName }) => `/database/collections?user_token=${userToken}&project_name=${projectName}`,
            providesTags: ['Database']
        }),

        // Airflow endpoints
        getAirflowDAGs: builder.query({
            query: ({ userToken, projectName }) => `/airflow/dags?user_token=${userToken}&project_name=${projectName}`,
            providesTags: ['Airflow']
        }),

        createAirflowDAG: builder.mutation({
            query: ({ userToken, projectName, dagData }) => ({
                url: `/airflow/dags?user_token=${userToken}&project_name=${projectName}`,
                method: 'POST',
                body: dagData
            }),
            invalidatesTags: ['Airflow']
        }),

        triggerAirflowDAG: builder.mutation({
            query: ({ userToken, projectName, dagId }) => ({
                url: `/airflow/dags/${dagId}/trigger?user_token=${userToken}&project_name=${projectName}`,
                method: 'POST'
            }),
            invalidatesTags: ['Airflow']
        }),

        // Chatbot (RAG) endpoints - N2NAI: /chatbots/list, /chatbots/create
        getChatbots: builder.query({
            query: () => '/chatbots/list',
            providesTags: ['Chatbot']
        }),

        createChatbot: builder.mutation({
            query: (body) => ({
                url: '/chatbots/create',
                method: 'POST',
                body: { name: body.name, description: body.description, llm_model: body.llm_model, embedding_model: body.embedding_model, vector_store_type: body.vector_store_type }
            }),
            invalidatesTags: ['Chatbot']
        }),

        getChatbot: builder.query({
            query: (chatbotId) => `/chatbots/${chatbotId}`,
            providesTags: ['Chatbot']
        }),

        updateChatbot: builder.mutation({
            query: ({ chatbotId, ...body }) => ({
                url: `/chatbots/${chatbotId}`,
                method: 'PUT',
                body
            }),
            invalidatesTags: ['Chatbot']
        }),

        deleteChatbot: builder.mutation({
            query: (chatbotId) => ({
                url: `/chatbots/${chatbotId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Chatbot']
        }),

        addDocumentsToChatbot: builder.mutation({
            query: ({ chatbotId, fileIds }) => ({
                url: `/chatbots/${chatbotId}/documents`,
                method: 'POST',
                body: { file_ids: fileIds }
            }),
            invalidatesTags: ['Chatbot']
        }),

        // Model Serving (N2NAI: /model-serving/list, no empty path)
        getModelServingList: builder.query({
            query: () => '/model-serving/list',
            providesTags: ['ModelServing']
        }),

        getModelServingTemplates: builder.query({
            query: () => '/model-serving/templates/',
            providesTags: ['ModelServing']
        }),

        deployModel: builder.mutation({
            query: (body) => ({
                url: '/model-serving/deploy',
                method: 'POST',
                body
            }),
            invalidatesTags: ['ModelServing']
        }),

        undeployModel: builder.mutation({
            query: (modelId) => ({
                url: `/model-serving/${modelId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['ModelServing']
        }),

        getModelLogs: builder.query({
            query: (modelId) => `/model-serving/${modelId}/logs`,
            providesTags: ['ModelServing']
        }),

        healthCheckModel: builder.mutation({
            query: (modelId) => ({
                url: `/model-serving/${modelId}/health`,
                method: 'POST'
            }),
            invalidatesTags: ['ModelServing']
        }),

        // Menu (dynamic menu widget - khi REACT_APP_MENU_API=true)
        getMenuWidget: builder.query({
            query: () => '/menu/widget',
            providesTags: ['Menu']
        }),

        // Datahub projects (RBAC-aware - params: user_token, permissions, groups)
        getDatahubProjects: builder.query({
            query: (params) => {
                const q = new URLSearchParams();
                if (params?.user_token) q.set('user_token', params.user_token);
                if (params?.permissions) q.set('permissions', params.permissions);
                if (params?.groups) q.set('groups', params.groups);
                const query = q.toString();
                return query ? `/datahub/projects?${query}` : '/datahub/projects';
            },
            providesTags: ['Datahub']
        }),

        createDatahubProject: builder.mutation({
            query: (body) => ({
                url: '/datahub/projects',
                method: 'POST',
                body
            }),
            invalidatesTags: ['Datahub']
        }),

        updateDatahubProject: builder.mutation({
            query: ({ projectId, ...body }) => ({
                url: `/datahub/projects/${projectId}`,
                method: 'PUT',
                body
            }),
            invalidatesTags: ['Datahub']
        }),

        deleteDatahubProject: builder.mutation({
            query: ({ projectId, user_token, permissions, groups, audit }) => {
                const q = new URLSearchParams();
                if (user_token) q.set('user_token', user_token);
                if (permissions != null) q.set('permissions', typeof permissions === 'string' ? permissions : JSON.stringify(permissions));
                if (groups != null) q.set('groups', typeof groups === 'string' ? groups : JSON.stringify(groups));
                if (audit != null) q.set('audit', typeof audit === 'string' ? audit : JSON.stringify(audit));
                const query = q.toString();
                return {
                    url: query ? `/datahub/projects/${projectId}?${query}` : `/datahub/projects/${projectId}`,
                    method: 'DELETE'
                };
            },
            invalidatesTags: ['Datahub']
        }),

        getDatahubProjectStats: builder.query({
            query: (params) => {
                const q = new URLSearchParams();
                if (params?.user_token) q.set('user_token', params.user_token);
                if (params?.permissions) q.set('permissions', params.permissions);
                if (params?.groups) q.set('groups', params.groups);
                const query = q.toString();
                return query ? `/datahub/projects/stats?${query}` : '/datahub/projects/stats';
            },
            providesTags: ['Datahub']
        }),

        getDatahubStorageOverview: builder.query({
            query: (params) => {
                const q = new URLSearchParams();
                if (params?.projectId) q.set('projectId', params.projectId);
                if (params?.user_token) q.set('user_token', params.user_token);
                if (params?.permissions) q.set('permissions', params.permissions);
                if (params?.groups) q.set('groups', params.groups);
                const query = q.toString();
                return query ? `/datahub/storage/overview?${query}` : '/datahub/storage/overview';
            },
            providesTags: ['Datahub']
        }),

        uploadDatahubFile: builder.mutation({
            queryFn: async ({ projectId, file, path, ...meta }, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const formData = new FormData();
                formData.append('file', file);
                formData.append('path', path || '');
                if (meta.user_token) formData.append('user_token', meta.user_token);
                if (meta.permissions) formData.append('permissions', typeof meta.permissions === 'string' ? meta.permissions : JSON.stringify(meta.permissions));
                if (meta.groups) formData.append('groups', typeof meta.groups === 'string' ? meta.groups : JSON.stringify(meta.groups));
                if (meta.audit) formData.append('audit', typeof meta.audit === 'string' ? meta.audit : JSON.stringify(meta.audit));
                const url = projectId ? `${baseUrl}/datahub/storage/upload?projectId=${encodeURIComponent(projectId)}` : `${baseUrl}/datahub/storage/upload`;
                const res = await fetch(url, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({ detail: res.statusText }));
                    return { error: { status: res.status, data: err } };
                }
                const data = await res.json();
                return { data };
            },
            invalidatesTags: ['Datahub']
        }),

        getDatahubStorageDownload: builder.query({
            queryFn: async ({ projectId, fileId, ...params }, { getState }, _extraOptions, baseQuery) => {
                const baseUrl =
                    process.env.REACT_APP_API_URL ||
                    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');
                const token = authManager.getToken();
                const q = new URLSearchParams();
                if (projectId) q.set('projectId', projectId);
                if (params?.user_token) q.set('user_token', params.user_token);
                const query = q.toString();
                const res = await fetch(`${baseUrl}/datahub/storage/download/${fileId}${query ? `?${query}` : ''}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });
                if (!res.ok) return { error: { status: res.status, data: { detail: res.statusText } } };
                const blob = await res.blob();
                return { data: blob };
            }
        })
    })
});

// Export hooks for usage in components
export const {
    // Health
    useHealthCheckQuery,

    // Auth
    useLoginAccessTokenMutation,
    useLoginTestTokenQuery,

    // User
    useGetCurrentUserQuery,
    useUpdateUserMutation,

    // Project
    useGetProjectsQuery,
    useCreateProjectMutation,
    useGetProjectQuery,
    useUpdateProjectMutation,
    useDeleteProjectMutation,

    // Billing usage (POST)
    usePostBillingUsageMutation,

    // Project documents (uploaded files)
    useGetProjectDocumentsQuery,
    useUploadProjectDocumentMutation,
    useDeleteProjectDocumentMutation,
    useGetResearchDocsListQuery,
    useCreateResearchDocMutation,
    useGetResearchDocQuery,
    useUpdateResearchDocMutation,
    useDeleteResearchDocMutation,
    useGetResearchDocDocumentsQuery,
    useGetResearchDocTaskStatusQuery,
    useAddResearchDocDocumentsMutation,
    useRemoveResearchDocDocumentMutation,
    useAskResearchDocMutation,
    useGetResearchConversationsQuery,
    useGetResearchConversationDetailQuery,
    useCreateResearchConversationMutation,
    useDeleteResearchConversationMutation,
    useViewProjectFileMutation,
    useGetProjectUploadBlobQuery,

    // Project MongoDB / Air
    useGetProjectMongodbTablesQuery,
    useCreateProjectMongodbTablesMutation,
    useDeleteProjectMongodbTablesMutation,
    useGetProjectMongodbDocumentsQuery,
    useUpdateProjectMongodbDocumentsMutation,
    useDeleteProjectMongodbDocumentsMutation,
    useDeleteHubUserMutation,
    useCreateAirUserMutation,
    useDeleteAirUserMutation,
    useGetProjectAirDagsMutation,
    useInsertProjectAirDagMutation,
    useDeleteProjectAirDagMutation,
    useFetchProjectAirDagsMutation,
    useFetchProjectAirDagsNodesMutation,
    useFetchProjectAirDagsEdgesMutation,

    // Environment
    useGetEnvironmentsQuery,
    useCreateEnvironmentMutation,
    useDeleteEnvironmentMutation,
    useSetDefaultEnvironmentMutation,
    useGetEnvironmentPackagesQuery,
    useInstallPackagesMutation,
    useSearchPackagesQuery,
    useExportEnvironmentQuery,
    useImportEnvironmentMutation,
    useCloneEnvironmentMutation,

    // Server
    useGetServersQuery,
    useStartServerMutation,
    useStopServerMutation,
    useDeleteServerMutation,

    // RBAC
    useGetPermissionsQuery,
    useCreatePermissionMutation,
    useUpdatePermissionMutation,
    useDeletePermissionMutation,
    useGetRolesQuery,
    useCreateRoleMutation,
    useUpdateRoleMutation,
    useDeleteRoleMutation,
    useGetUsersQuery,
    useGetRBACUsersQuery,
    useCreateUserMutation,
    useUpdateRBACUserMutation,
    useDeleteUserMutation,
    useGetUserPermissionsQuery,
    useAssignRoleToUserMutation,
    useRemoveRoleFromUserMutation,
    useCheckPermissionMutation,

    // Billing
    useGetBillingUsageQuery,
    useGetBillingInvoicesQuery,

    // Storage
    useGetStorageFilesQuery,
    useUploadStorageFileMutation,
    useUploadFileMutation,
    useDeleteStorageFileMutation,
    useDeleteFileMutation,
    useBulkDeleteStorageFilesMutation,
    useUpdateStorageFileLifecycleMutation,
    useGetStorageAnalyticsQuery,
    useGetStorageMigrationStatusQuery,
    useStartStorageMigrationMutation,
    useGetLakehouseStatsQuery,
    useGetStorageFilePreviewQuery,
    useLazyGetStorageFilePreviewQuery,
    useCreateStorageFileShareMutation,
    useGetStorageFileSharesQuery,
    useDeleteStorageFileShareMutation,
    useSearchStorageFilesMutation,
    useCreateStorageFolderMutation,
    useGetStorageFoldersQuery,
    useMoveStorageFilesMutation,
    useCopyStorageFilesMutation,
    useDeleteStorageFolderMutation,
    useGetStorageQuotaQuery,
    useUpdateStorageQuotaMutation,

    // Model Serving
    useGetModelServingListQuery,
    useGetModelServingTemplatesQuery,
    useDeployModelMutation,
    useUndeployModelMutation,
    useGetModelLogsQuery,
    useLazyGetModelLogsQuery,
    useHealthCheckModelMutation,

    // Menu
    useGetMenuWidgetQuery,

    // Datahub
    useGetDatahubProjectsQuery,
    useCreateDatahubProjectMutation,
    useUpdateDatahubProjectMutation,
    useDeleteDatahubProjectMutation,
    useGetDatahubProjectStatsQuery,
    useGetDatahubStorageOverviewQuery,
    useUploadDatahubFileMutation,
    useGetDatahubStorageDownloadQuery,

    // Lakehouse Data Sources (MongoDB, Postgres, MinIO, Firebase Storage)
    useGetLakehouseSourcesQuery,
    useGetLakehouseSourceHealthQuery,

    // Project Data Sources (per-project: MongoDB, Postgres, MinIO, Firebase)
    useGetProjectDataSourcesQuery,
    useCreateProjectDataSourceMutation,
    useUpdateProjectDataSourceMutation,
    useDeleteProjectDataSourceMutation,
    useSyncLakehouseSamplesMutation,
    useFetchProjectDataSourceQuery,
    useLazyFetchProjectDataSourceQuery,

    // Database
    useGetDatabaseStatsQuery,
    useGetCollectionsQuery,

    // Airflow
    useGetAirflowDAGsQuery,
    useCreateAirflowDAGMutation,
    useTriggerAirflowDAGMutation,

    // Chatbot (RAG)
    useGetChatbotsQuery,
    useCreateChatbotMutation,
    useGetChatbotQuery,
    useUpdateChatbotMutation,
    useDeleteChatbotMutation,
    useAddDocumentsToChatbotMutation
} = apiSlice;

export default apiSlice;
